import React, { useContext, useEffect, useState } from 'react';
import {
	IOfferContext,
	ITypeVignetteOrderingProcessFields,
	OfferContext,
	VignetteContext,
	countryUrlMap,
	fetchInitialVignetteData,
	getSupplierIdByCountry,
	setRouteLangParam,
	getVignetteOrderingProcessCollection,
	Locales,
	resetStorageAndContext,
	getUrlBasePath,
} from '@adac-mp/shared';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { ContentLoader, getDrawerHelperData, withLocalization } from '@adac-mp/components';
import { trackFunnelStart, trackFunnelView } from '@adac-mp/components/src/utils/tracking/tracking';
import { CategorySelection } from '../../components/vignette/categorySelection';
import Configuration from '../../components/configuration';
import { TollRoutesSelection } from '../../components/vignette/tollRoutesSelection';
import { ProductSelection } from '../../components/vignette/selection';
import { TimeSelection } from '../../components/vignette/timeSelection';
import { PlatesSelection } from '../../components/vignette/platesSelection';

const VignetteConfigurationAutComponent = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const supplierId = getSupplierIdByCountry(location);
	const [reuse, setReuse] = useState<boolean>(false);
	const {
		selectedProducts,
		cartItems,
		contentfulLabels,
		updateIsTransitionPeriod,
		updateContentfulLabels,
		deleteSelectedProducts,
		updateCart,
	} = useContext(VignetteContext);
	const { lang } = useParams();
	const { updateVignetteOffers, updateAllCountries, updateAllOffers, updateTollSectionOffers } =
		useContext<IOfferContext>(OfferContext);
	const { error, data, loading } = useQuery(getVignetteOrderingProcessCollection, {
		variables: { lang: lang || 'de', slug: 'oesterreich' },
	});
	const currentLabels = contentfulLabels as ITypeVignetteOrderingProcessFields;
	const currSelectedProduct = selectedProducts[0];

	useEffect(() => {
		resetStorageAndContext(
			supplierId,
			deleteSelectedProducts,
			updateCart,
			updateTollSectionOffers,
			updateIsTransitionPeriod,
		);

		fetchInitialVignetteData(getSupplierIdByCountry(location), {
			navigate,
			lang: lang as Locales,
			updateVignetteOffers,
			updateAllOffers,
			updateAllCountries,
			updateTollSectionOffers,
			updateIsTransitionPeriod,
		});
	}, [lang]);

	useEffect(() => {
		setReuse(cartItems.length > 0);
		if (cartItems.length > 0) {
			trackFunnelView({
				label:
					currentLabels?.tollCategoryReuseHeadline ||
					'Für welches Fahrzeug benötigen Sie das weitere Produkt?',
				element: 'Reuse Fahrzeugauswahl',
				trackOnce: true,
			});
		} else {
			trackFunnelStart({
				label: currentLabels?.tollCategoriesHeadline || 'Mit welchem Fahrzeug sind Sie unterwegs?',
				element: 'Fahrzeugauswahl',
				trackOnce: true,
			});
		}
	}, [cartItems]);

	useEffect(() => {
		if (!error) {
			updateContentfulLabels(data?.vignetteOrderingProcessCollection.items[0]);
		}
	}, [data]);

	const onFinished = () => {
		navigate(getUrlBasePath(`/${countryUrlMap[supplierId]}/product-done${setRouteLangParam(lang)}`));
	};

	// Props added dynamically in Configuration component
	const componentList = [
		{
			title: `${cartItems.length > 0 ? 'Reuse ' : ''}Fahrzeugauswahl`,
			component: (
				<CategorySelection
					key="step0"
					helpContent={getDrawerHelperData('vehicleTypes', currentLabels?.helpDrawers)}
				/>
			),
		},
		{
			title: 'Produktauswahl',
			component: (
				<ProductSelection
					key="step1"
					supplierId={supplierId}
					helpContent={getDrawerHelperData('productSelection', currentLabels?.helpDrawers)}
					tracking={{
						funnel_view: {
							label: currentLabels?.headlineProductSelection || 'Was benötigen Sie?',
							element: 'Produktauswahl',
							trackOnce: true,
						},
					}}
				/>
			),
		},
	];

	if (currSelectedProduct?.moduleTypeId !== 4) {
		componentList.push({
			title: currSelectedProduct?.moduleTypeId === 3 ? 'Streckenauswahl' : 'Gültigkeit',
			component:
				currSelectedProduct?.moduleTypeId === 3 ? (
					<TollRoutesSelection
						key="step2"
						helpContent={getDrawerHelperData('tollSection', currentLabels?.helpDrawers)}
						tracking={{
							funnel_view: {
								label: currentLabels?.headlineTollSection || 'Wählen Sie eine oder mehrere Strecken',
								element: `Streckenauswahl`,
								trackOnce: true,
							},
						}}
					/>
				) : (
					<TimeSelection
						key="step2"
						helpContent={getDrawerHelperData('vignetteDate', currentLabels?.helpDrawers)}
						tracking={{
							funnel_view: {
								label: currentLabels?.headlineStartDate || 'Ab wann benötigen Sie die Vignette?',
								element: 'Gültigkeit',
								trackOnce: true,
							},
						}}
					/>
				),
		});
	}

	if (!reuse) {
		componentList.push({
			title: 'Kennzeichen',
			component: (
				<PlatesSelection
					key="step3"
					hasSpecialPlates
					tracking={{
						funnel_view: {
							label: currentLabels?.headlineLicenseplate || 'Wie lautet Ihr Kennzeichen?',
							element: 'Kennzeichen',
							trackOnce: true,
						},
					}}
				/>
			),
		});
	}

	return (
		<>
			{loading ? (
				<ContentLoader isLoading hasPortal={false} />
			) : (
				<Configuration
					withStepReset
					supplierHasMultipleProducts
					reuse={reuse}
					componentList={componentList}
					supplierId={supplierId}
					setReuse={setReuse}
					onFinished={onFinished}
				/>
			)}
		</>
	);
};

export const VignetteConfigurationAut = withLocalization(VignetteConfigurationAutComponent);
